import React from 'react';
import marked from 'marked';
import { Link } from 'gatsby';

import CDNImage from '../../components/common/cdn-image';
import DigitalRightsBlock from './digital-rights-block';

import SourceIcon from '../../../images/collection-icons/source_2x.png';
import HatTipIcon from '../../../images/collection-icons/hattip_2x.png';
import DownloadIcon from '../../../images/collection-icons/download_2x.png';
import RightsIcon from '../../../images/collection-icons/rights_2x.png';

import '../../styles/collections/meta-box.scss';

const aggregatedCollectionCount = (source) => {
	if (!source) {
		return 0;
	}
	let collections = source.Collections || [];
  
	let childSources = source.data ? source.data.Child_Sources : source.Child_Sources;

	if (childSources && childSources.length > 0) {
		childSources.forEach(childSource => {
			collections = collections.concat(childSource.data.Collections);
		});
	}
  
	return collections.length;
};

const specialCaseDirectFromAggregator = (sources) => {
	// Determine if there is only one source and it is aggregator
	if (sources.length === 1) {
		if (sources[0].data.Categories && sources[0].data.Categories.includes('Aggregator')) {
			return true;
		}
	}
  
	return false;
};

const filterForAggregator = (sources) => {
	const filteredSources = sources.filter(({ data }) => data.Categories && data.Categories.includes('Aggregator'));
  
	if (filteredSources.length > 0) {
		return filteredSources[0];
	}
  
	return null;
};

const variousCheck = (sources) => {
	const filteredAggregator = filterForAggregator(sources);
	const filteredProvider = sources.filter(({ data }) => !data.Categories || !data.Categories.includes('Aggregator'));
  
	if (sources.length > 2) {
		return true;
	} else if (sources.length === 2 && (!filteredAggregator || filteredProvider.length !== 1)) {
		return true;
	} else if (sources.length === 1 && sources[0].data.Title === 'Various') {
		return true;
	}
  
	return false;
};

const buildSourceLink = function(link, sources) {
	let label;

	if (!sources || sources.length === 0) {
		return null;
	}

	if (sources.length === 1) {
		if (sources[0].data.Umbrella_Title && sources[0].data.Umbrella_Title.length > 0) {
			label = sources[0].data.Umbrella_Title;
		} else {
			label = sources[0].data.Title;
		}
	} else if (sources.length === 2) {
		const filteredAggregator = filterForAggregator(sources);
		const filteredProvider = sources.filter(({ data }) => !data.Categories || !data.Categories.includes('Aggregator'));

		if (filteredAggregator && filteredProvider.length === 1) {
			const displayTitle = (filteredProvider[0].data.Umbrella_Title && filteredProvider[0].data.Umbrella_Title.length > 0)
				? filteredProvider[0].data.Umbrella_Title
				: filteredProvider[0].data.Title;
			label = `${filteredAggregator.data.Title} / ${displayTitle}`;
		} else {
			label = 'Various';
		}
	} else {
		label = 'Various';
	}

	if (link) {
		if (link.substring(0, 4) !== 'http') {
			// Handle the case where the Source Link column has been being used to specify a custom
			// source text (without support). Now check to see if the link has been specified as something
			// other than a URL and then just return that as if it were an unlinked label.
			return link;
		}
	
		return (<a href={link} target="_blank" rel="noopener noreferrer">{label}</a>);
	}

	return label;
}


const showSourcesList = (sources) => {
	if (sources.length > 1) {
		return true;
	} else {
		if (sources[0].data.Title === 'Various') {
			return false;
		}
	}
  
	return true;
}

const MetaBox = (props) => {
	const sourceListingLinkFromSlug = (slug) => {
		const filteredSourceEdges = props.sources.edges.filter(({ node }) => node.data.Slug === slug);
	

		if (filteredSourceEdges && filteredSourceEdges.length > 0) {
			const filteredSource = filteredSourceEdges[0].node.data;
			const displayTitle = (filteredSource.Umbrella_Title && filteredSource.Umbrella_Title.length > 0) ? filteredSource.Umbrella_Title : filteredSource.Title;
			const sourceSlug = (filteredSource.Umbrella_Slug && filteredSource.Umbrella_Slug.length > 0) ? filteredSource.Umbrella_Slug : filteredSource.Slug;
			const logoUrl = (filteredSource.Umbrella_Logo_URL && filteredSource.Umbrella_Logo_URL.length > 0) ? filteredSource.Umbrella_Logo_URL : filteredSource.Logo_URL;
			const collectionCount = (filteredSource.Umbrella_Source && filteredSource.Umbrella_Source.length > 0) ? aggregatedCollectionCount(filteredSource.Umbrella_Source[0]) : aggregatedCollectionCount(filteredSource);
			
			if (filteredSource.Collections &&
				filteredSource.Collections.length > 0 &&
				filteredSource.Title !== 'Various') {
				return (
					<li>
						<Link to={`/collections/source/${sourceSlug}`}>
							<div className="logo">
								{logoUrl && (
									<CDNImage
										imagePath={logoUrl}
										alt={`${displayTitle} logo`}
									/>
								)}
							</div>
							<div className="text">
								More {displayTitle} content on PDR ({collectionCount})
							</div>
						</Link>
					</li>
				);
			}
			return null;
		}

		return null;
	};

	let underlyingWorksProfiles = [];
	if (props.collection.data.Rights_Profiles) {
		underlyingWorksProfiles = props.collection.data.Rights_Profiles.filter(({ data }) => {
			return data.Group === 'Underlying Work';
		});
	}
  
	if (props.collection.data.Sources || props.collection.data.Rights_Profiles || 
		props.collection.data.Download || props.collection.data.Hat_Tip) {
		
		return (
			<div className="collection__meta">
				{(props.collection.data.Sources) ? (
					<div className="collection__meta__sources">
						<table className="data">
							<tbody>
								<tr>
									<td className="icon">
										<img
											src={ SourceIcon }
											alt="Source"
										/>
									</td>
									<td className="label">
										Source
									</td>
									<td className="content">
										{buildSourceLink(props.collection.data.Source_Link, props.collection.data.Sources)}
									</td>
								</tr>
							</tbody>
						</table>
						{(showSourcesList(props.collection.data.Sources)) ? (
							<ul className="sources-list">
								{props.collection.data.Sources.map(({ data }, idx) => (
									<>{sourceListingLinkFromSlug(data.Slug)}</>
								))}
							</ul>
						) : null}
					</div>
				) : null}

				<div className="collection__meta__details">
					<table className="data">
						<tbody>
							{(underlyingWorksProfiles.length > 0) ? (
								<tr>
									<td className="icon">
										<img
											src={ RightsIcon }
											alt="Rights"
										/>
									</td>
									<td className="label">
										Underlying Work Rights
									</td>
									<td className="content">
										{underlyingWorksProfiles.map(({ data }, idx) => (
											<>
												<Link
													key={`underlying-link-${idx}`}
													to={`/rights-labelling-on-our-site#${data.Slug}`}
												>
													{data.Label}
												</Link>
												{(idx < underlyingWorksProfiles.length - 1) && (
													<span>&nbsp;&nbsp;/&nbsp;&nbsp;</span>
												)}
											</>
										))}
									</td>
								</tr>
							) : null}
							<tr>
								<td className="icon">
									{(underlyingWorksProfiles.length === 0) ? (
										<img
											src={ RightsIcon }
											alt="Rights"
										/>
									) : null}
								</td>
								<td className="label top-aligned">
									Digital Copy Rights
								</td>
								<td className="content top-aligned">
									{variousCheck(props.collection.data.Sources) ? (
										<div>
											Various – see source links.
										</div>
									) : (
										<>
											{specialCaseDirectFromAggregator(props.collection.data.Sources) && (
												<DigitalRightsBlock
													source={props.collection.data.Sources[0]}
													aggregator={props.collection.data.Sources[0]}
												/>
											)}
											{!specialCaseDirectFromAggregator(props.collection.data.Sources) && (
												<>
													{props.collection.data.Sources.map(source => (
														<>
															{source.data.Categories[0] === 'Provider' && (
																<DigitalRightsBlock
																	source={source}
																	aggregator={filterForAggregator(props.collection.data.Sources)}
																/>
															)}
														</>
													))}
												</>
											)}
										</>
									)}
								</td>
							</tr>
							{(props.collection.data.Download) ? (
								<tr>
									<td className="icon">
										<img
											src={ DownloadIcon }
											alt="Download"
										/>
									</td>
									<td className="label">
										Download
									</td>
									<td
										className="content"
										dangerouslySetInnerHTML={
											{ __html: marked(props.collection.data.Download).replace('<p>', '').replace('</p>', '')}}
									/>
								</tr>
							): null}
							{(props.collection.data.Hat_Tip) ? (
								<tr>
									<td className="icon">
										<img
											src={ HatTipIcon }
											alt="Found Via"
										/>
									</td>
									<td className="label">
										Found Via
									</td>
									<td
										className="content"
										dangerouslySetInnerHTML={{ __html: marked(props.collection.data.Hat_Tip)}}
									/>
								</tr>
							) : null}
						</tbody>
					</table>
				</div>
			</div>
		);
	}

	return null;
};

export default MetaBox;